"use client";
import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Home, Wifi, Smartphone, Shield, Zap, Settings } from "lucide-react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { FaUserTie, FaUserAlt } from "react-icons/fa";
import TechStackAccordionForSmartHome from "./TechStackAccordionForSmartHome";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../../Components/ThemeContext";
import SEOHead from "../../Components/SEOHead";

const smartHomeSchema = JSON.stringify({
  "@context": "https://schema.org",
  "@type": "Service",
  "name": "Smart Home Systems",
  "provider": { "@type": "Organization", "name": "Fiieve", "url": "https://fiieve.com" },
  "description": "Fiieve designs and builds custom smart home and building automation systems — integrating lighting, HVAC, security, and energy management via Home Assistant, KNX, Zigbee, and Z-Wave.",
  "areaServed": "GB",
  "serviceType": "Smart Home Systems",
  "url": "https://fiieve.com/engineering/smart-home",
  "breadcrumb": {
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home",              "item": "https://fiieve.com/" },
      { "@type": "ListItem", "position": 2, "name": "Services",          "item": "https://fiieve.com/services" },
      { "@type": "ListItem", "position": 3, "name": "Engineering",       "item": "https://fiieve.com/services#engineering" },
      { "@type": "ListItem", "position": 4, "name": "Smart Home Systems","item": "https://fiieve.com/engineering/smart-home" }
    ]
  }
});

const lightGridStyle = { backgroundImage: `linear-gradient(rgba(74,222,128,0.07) 1px, transparent 1px), linear-gradient(90deg, rgba(74,222,128,0.07) 1px, transparent 1px)`, backgroundSize: "40px 40px" };
const darkGridStyle  = { backgroundImage: `linear-gradient(rgba(74,222,128,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(251,146,60,0.05) 1px, transparent 1px)`, backgroundSize: "40px 40px" };
const fadeUp = { hidden: { opacity: 0, y: 50 }, show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } } };

function CountUp({ target, duration = 1200 }) {
  const [display, setDisplay] = useState("0");
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  useEffect(() => {
    if (!inView) return;
    if (target.includes("–") || target.includes("/")) { const t = setTimeout(() => setDisplay(target), 400); return () => clearTimeout(t); }
    const numeric = parseFloat(target.replace(/[^\d.]/g, "")); const suffix = target.replace(/[\d.]/g, ""); const isDecimal = target.includes(".");
    let start = 0; const inc = numeric / (duration / 16);
    const timer = setInterval(() => { start += inc; if (start >= numeric) { setDisplay(`${isDecimal ? numeric.toFixed(1) : numeric}${suffix}`); clearInterval(timer); } else setDisplay(`${isDecimal ? start.toFixed(1) : Math.floor(start)}${suffix}`); }, 16);
    return () => clearInterval(timer);
  }, [inView, target, duration]);
  return <span ref={ref}>{display}</span>;
}

function ServiceCard({ icon: Icon, title, description, isDark }) {
  return (
    <motion.div variants={fadeUp} whileHover={{ scale: 1.05 }} transition={{ type: "spring", stiffness: 200, damping: 12 }}
      className={`${isDark ? "bg-gray-800" : "bg-[#1a2e1a]"} border-2 border-green-400 rounded-lg p-6 hover:border-green-300 transition-all duration-300 h-full`}>
      <div className="flex items-start gap-4 w-full">
        <div className="bg-green-400 p-2.5 rounded-lg flex-shrink-0 self-start"><Icon className="w-6 h-6 text-black" /></div>
        <div><h3 className="text-green-400 text-base font-bold mb-2 leading-tight">{title}</h3><p className="text-gray-300 text-xs leading-relaxed">{description}</p></div>
      </div>
    </motion.div>
  );
}

export default function SmartHomePage() {
  const { isDark } = useTheme();
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const navigate = useNavigate();

  const greenHeading = isDark ? "text-green-400" : "text-green-700";
  const headingText  = isDark ? "text-white"     : "text-gray-900";
  const bodyText     = isDark ? "text-gray-400"  : "text-gray-600";
  const statCardBg   = isDark ? "bg-gray-800/80" : "bg-[#1a2e1a]";
  const testimCardBg = isDark ? "bg-[#0a0a0a]"  : "bg-[#1a2e1a]";
  const ctaBoxBg     = isDark ? "bg-black"       : "bg-[#1a2e1a]";
  const techCardBg   = isDark ? "bg-black"       : "bg-[#1a2e1a]";

  const services = [
    {
      icon: Home,
      title: "Home Automation Design",
      description: "Full home automation design and installation — integrating lighting, blinds, HVAC, audio, and security into a single intelligent control system built on Home Assistant or KNX.",
    },
    {
      icon: Wifi,
      title: "Zigbee, Z-Wave & Matter",
      description: "Wireless smart home protocol integration — Zigbee, Z-Wave, and Matter device pairing, mesh network setup, and hub configuration for reliable, low-latency device control.",
    },
    {
      icon: Smartphone,
      title: "Mobile & Voice Control",
      description: "Custom mobile apps and voice assistant integration with Amazon Alexa, Google Home, and Apple HomeKit — giving you full control of your smart home from any device.",
    },
    {
      icon: Zap,
      title: "Energy Management",
      description: "Smart energy monitoring and management — solar integration, EV charger control, battery storage, time-of-use scheduling, and real-time consumption dashboards.",
    },
    {
      icon: Shield,
      title: "Smart Security Systems",
      description: "Integrated smart security with IP cameras, motion sensors, smart locks, alarm systems, and real-time notifications — all unified in a single secure control interface.",
    },
    {
      icon: Settings,
      title: "Commercial Building Automation",
      description: "Building management systems (BMS) for offices, hotels, and retail — HVAC control, access management, occupancy sensing, and energy optimisation at scale.",
    },
  ];

  const stats = [
    { number: "50+",  label: "Smart Homes Delivered", icon: "/Images/satisfaction.png" },
    { number: "30%",  label: "Average Energy Saving",  icon: "/Images/roi.png" },
    { number: "100+", label: "Devices Integrated",     icon: "/Images/time.png" },
    { number: "24/7", label: "Remote Monitoring",      icon: "/Images/award.png" },
  ];

  const testimonials = [
    {
      id: 1, name: "Marcus Webb",   handle: "@marcus_smarthome", gender: "male",
      title: "Incredible Smart Home Setup",
      feedback: "Fiieve transformed our home into a fully automated living space. Lighting, heating, security, and entertainment — all controlled from one app. The installation was seamless and the system has been rock solid.",
    },
    {
      id: 2, name: "Fatima Al-Sayed", handle: "@fatima_building", gender: "female",
      title: "Outstanding BMS for Our Office",
      feedback: "They built a complete building automation system for our three-floor office. Energy costs dropped by 28% in the first quarter. The dashboard gives us total visibility over everything. Exceptional work.",
    },
  ];

  const technologies = [
    {
      title: "Smart Home Platforms",
      description: "Open and proprietary home automation platforms and protocols",
      tools: ["Home Assistant", "KNX", "Zigbee2MQTT", "Z-Wave JS", "Apple HomeKit"],
      benefits: ["Single unified control interface", "Local-first — no cloud dependency", "Thousands of compatible devices", "Fully customisable automations"],
    },
    {
      title: "Connectivity & Integration",
      description: "Communication protocols and cloud integration for smart home systems",
      tools: ["Zigbee", "Z-Wave", "Matter / Thread", "MQTT", "Google Home / Alexa"],
      benefits: ["Reliable mesh networking", "Multi-protocol device support", "Voice control integration", "Remote access and monitoring"],
    },
  ];

  const techTags = ["Home Assistant", "KNX", "Zigbee", "Z-Wave", "Matter", "MQTT", "Alexa"];
  const sliderSettings = { dots: true, infinite: false, speed: 800, slidesToShow: 2, slidesToScroll: 1, autoplay: false, responsive: [{ breakpoint: 768, settings: { slidesToShow: 1, slidesToScroll: 1 } }] };

  return (
    <>
      <SEOHead
        title="Smart Home Systems UK | Home Assistant, KNX, Zigbee | Fiieve"
        description="Fiieve designs and installs custom smart home and building automation systems using Home Assistant, KNX, Zigbee, Z-Wave, and Matter. Lighting, HVAC, security, and energy management — fully integrated."
        canonical="https://fiieve.com/engineering/smart-home"
        ogImage="https://fiieve.com/Images/og-smarthome.jpg"
        schema={smartHomeSchema}
      />
      <main className={`relative overflow-hidden pt-20 sm:pt-24 lg:pt-28 transition-colors duration-300 ${isDark ? "bg-black text-white" : "bg-[#f0f4f0] text-gray-900"}`} style={isDark ? darkGridStyle : lightGridStyle}>
        {!isDark && (<div className="pointer-events-none fixed inset-0 overflow-hidden" style={{ zIndex: 0 }}><div style={{ position:"absolute", top:"-10%", left:"-5%", width:"55vw", height:"55vw", background:"radial-gradient(circle, rgba(74,222,128,0.13) 0%, transparent 70%)", filter:"blur(40px)" }} /><div style={{ position:"absolute", top:"-8%", right:"-8%", width:"50vw", height:"50vw", background:"radial-gradient(circle, rgba(251,146,60,0.11) 0%, transparent 70%)", filter:"blur(40px)" }} /></div>)}

        <div className="relative" style={{ zIndex: 1 }}>

          {/* ── HERO ── */}
          <div className="relative h-[25vh] overflow-hidden" style={{ marginBottom: "-3px" }}>
            <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url('/Images/bg2.png')" }} aria-hidden="true" />
            <div className="absolute inset-0 bg-black/80" aria-hidden="true" />
            <div className="relative mx-auto max-w-7xl px-6 md:px-8 h-full flex flex-col items-center justify-center text-center">
              <h1 className="text-3xl md:text-5xl font-extrabold text-green-400">Smart Home Systems</h1>
              <nav aria-label="Breadcrumb" className="mt-3">
                <ol className="flex items-center justify-center gap-1 text-sm text-white" role="list">
                  <li><a href="/" className="hover:text-green-400 transition-colors">Home</a></li>
                  <li aria-hidden="true" className="text-gray-400">/</li>
                  <li><a href="/services" className="hover:text-green-400 transition-colors">Services</a></li>
                  <li aria-hidden="true" className="text-gray-400">/</li>
                  <li className="text-gray-400">Engineering</li>
                  <li aria-hidden="true" className="text-gray-400">/</li>
                  <li aria-current="page" className="text-green-400">Smart Home Systems</li>
                </ol>
              </nav>
            </div>
          </div>

          {/* ── INTRO ── */}
          <motion.section aria-labelledby="smarthome-intro-heading" className="pt-10 pb-4 px-6 max-w-7xl mx-auto text-center" variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}>
            <p className={`text-sm font-semibold uppercase tracking-widest ${greenHeading} mb-2`}>Home Automation • Lighting • HVAC • Security • Energy</p>
            <h2 id="smarthome-intro-heading" className={`text-2xl sm:text-3xl md:text-4xl font-bold ${headingText} mb-5`}>
              From Lights to HVAC — <span className={greenHeading}>Every Room, Every Device, One Intelligent System</span>
            </h2>
            <p className={`${bodyText} text-lg md:text-xl leading-relaxed mb-8`}>
              Whether you want to automate a single apartment or an entire commercial building, Fiieve designs and builds custom smart home and building automation systems — integrating <strong className={headingText}>lighting</strong>, <strong className={headingText}>HVAC</strong>, <strong className={headingText}>security</strong>, and <strong className={headingText}>energy management</strong> via <strong className={headingText}>Home Assistant</strong>, <strong className={headingText}>KNX</strong>, <strong className={headingText}>Zigbee</strong>, and <strong className={headingText}>Z-Wave</strong>. Full control from your phone, tablet, or voice — anywhere in the world.
            </p>
            <div className="inline-flex flex-wrap justify-center items-center gap-1 p-1.5 rounded-full" style={{ background: isDark ? "#111" : "#0f1f0f", border: "1px solid rgba(74,222,128,0.25)" }} aria-label="Technologies used in smart home systems">
              {techTags.map((tag) => (<span key={tag} className="px-4 py-1.5 rounded-full text-base font-semibold text-white/80 hover:text-white hover:bg-white/10 transition-colors duration-200 cursor-default">{tag}</span>))}
            </div>
          </motion.section>

          {/* ── SERVICES ── */}
          <section aria-labelledby="services-heading" className="pt-8 pb-12 px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-8">
                <h2 className={`text-xl sm:text-2xl font-semibold ${headingText} mb-1`}>What We Build</h2>
                <h2 id="services-heading" className={`${greenHeading} text-3xl md:text-4xl lg:text-5xl font-bold`}>Our Smart Home Services</h2>
              </div>
              <motion.div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch" initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}>
                {services.map((s, i) => <ServiceCard key={i} icon={s.icon} title={s.title} description={s.description} isDark={isDark} />)}
              </motion.div>
            </div>
          </section>

          {/* ── STATS ── */}
          <section aria-labelledby="stats-heading" className="pb-16">
            <div className="max-w-7xl mx-auto px-6 text-center">
              <motion.div className="mb-10" variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}>
                <h2 className={`text-xl sm:text-2xl font-semibold ${headingText} mb-2`}>Our Track Record</h2>
                <h2 id="stats-heading" className={`${greenHeading} text-3xl md:text-4xl lg:text-5xl font-bold`}>Smart Home Results in Numbers</h2>
              </motion.div>
              <motion.div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10" variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}>
                {stats.map((card, i) => (
                  <motion.div key={i} whileHover={{ scale: 1.08, boxShadow: "0 0 30px rgba(74,222,128,0.6)" }} transition={{ type: "spring", stiffness: 200, damping: 12 }} className={`border border-green-400/50 rounded-2xl p-8 text-center hover:border-green-400 transition-all duration-300 ${statCardBg} flex flex-col items-center`}>
                    <img src={card.icon} alt={card.label} className="w-20 h-20 mb-4 object-contain" loading="lazy" width={80} height={80} />
                    <div className="text-4xl font-extrabold text-green-400 mb-2"><CountUp target={card.number} /></div>
                    <p className="text-gray-300 text-lg font-semibold">{card.label}</p>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </section>

          {/* ── TESTIMONIALS ── */}
          <section aria-labelledby="testimonials-heading">
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="max-w-6xl mx-auto px-4 mb-16">
              <h2 className={`text-xl sm:text-2xl font-semibold ${headingText} text-center mb-2`}>Client Results</h2>
              <h2 id="testimonials-heading" className={`${greenHeading} text-3xl md:text-4xl font-bold text-center mb-8 md:mb-12`}>What Our Clients Say</h2>
              {!isDark && <style>{`.slick-prev:before, .slick-next:before { color: #166534 !important; }`}</style>}
              <Slider {...sliderSettings}>
                {testimonials.map((t) => (
                  <div key={t.id} className="px-3">
                    <motion.article initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} aria-label={`Testimonial from ${t.name}`}
                      className={`${testimCardBg} border border-green-400/30 rounded-2xl p-6 md:p-8 shadow-lg hover:shadow-green-400/10 hover:border-green-400/60 transition-all duration-300 flex flex-col sm:flex-row items-center sm:items-start gap-5`}>
                      <div className="w-14 h-14 md:w-16 md:h-16 rounded-full border-2 border-green-400/40 bg-[#0d1a0f] flex items-center justify-center flex-shrink-0" aria-hidden="true">
                        {t.gender === "male" ? <FaUserTie size={28} className="text-green-400" /> : <FaUserAlt size={24} className="text-green-300" />}
                      </div>
                      <div className="text-center sm:text-left flex flex-col gap-2">
                        <h3 className="text-lg font-bold text-white">{t.title}</h3>
                        <p className="text-gray-400 text-sm leading-relaxed">{t.feedback}</p>
                        <div className="flex items-center justify-center sm:justify-start gap-0.5 mt-1" aria-label="5 star rating">
                          {Array(5).fill().map((_, i) => <span key={i} className="text-green-400 text-lg" aria-hidden="true">★</span>)}
                        </div>
                        <p className="text-green-400 font-semibold text-sm">{t.name}</p>
                        <p className="text-gray-500 text-xs">{t.handle}</p>
                      </div>
                    </motion.article>
                  </div>
                ))}
              </Slider>
            </motion.div>
          </section>

          {/* ── CTA ── */}
          <motion.section aria-label="Start your smart home project" initial={{ opacity: 0, y: 50 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="max-w-5xl mx-auto px-4 mb-16">
            <div className={`${ctaBoxBg} border-2 border-green-400 rounded-2xl p-10 flex flex-col items-center gap-6 text-center`}>
              <div className="w-full max-w-3xl">
                <h2 className="text-xl md:text-2xl font-bold text-white mb-3">Ready to Make Your Home Smarter?</h2>
                <p className="text-gray-300 text-base md:text-lg leading-relaxed">Whether it's a single room, a full home, or a commercial building — our smart home engineers design, install, and configure everything from scratch. Get in touch today.</p>
              </div>
              <button onClick={() => navigate("/contact-us")} aria-label="Contact Fiieve to start your smart home project" className="bg-green-500 hover:bg-green-600 text-black font-bold py-3 px-6 rounded-lg transition-all duration-300">
                Discuss Your Smart Home Project
              </button>
            </div>
          </motion.section>

          {/* ── TECHNOLOGIES ── */}
          <section aria-labelledby="tech-heading" className="py-6 md:py-8 px-4 -mt-12 md:-mt-16">
            <div className="max-w-7xl mx-auto">
              <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="text-center mb-10">
                <h2 className={`text-xl sm:text-2xl font-semibold ${headingText} mb-2`}>Our Smart Home Stack</h2>
                <h2 id="tech-heading" className={`${greenHeading} text-3xl md:text-4xl lg:text-5xl font-bold mb-4`}>Technologies We Use</h2>
                <p className={`${bodyText} text-lg max-w-2xl mx-auto`}>We use Home Assistant, KNX, Zigbee, Z-Wave, and Matter to build reliable, future-proof smart home systems.</p>
              </motion.div>
              <motion.div className="grid grid-cols-1 md:grid-cols-2 gap-6" initial="hidden" whileInView="show" viewport={{ once: true }} variants={{ show: { transition: { staggerChildren: 0.15 } } }}>
                {technologies.map((tech, i) => (
                  <motion.article key={i} aria-label={`${tech.title} technology stack`} className={`${techCardBg} border-2 border-green-400 rounded-2xl p-6 transition-all duration-300`}
                    whileHover={{ scale: 1.05, boxShadow: "0 0 25px 5px rgba(74,222,128,0.7)", borderColor: "#4ade80" }} transition={{ type: "spring", stiffness: 200, damping: 12 }}>
                    <h3 className="text-green-400 text-xl font-bold mb-2">{tech.title}</h3>
                    <p className="text-gray-400 mb-3">{tech.description}</p>
                    <p className="text-gray-300 font-semibold text-sm mb-1">Platforms & Tools:</p>
                    <p className="text-gray-300 mb-3">{tech.tools.join(", ")}</p>
                    <p className="text-gray-300 font-semibold text-sm mb-1">Benefits:</p>
                    <ul className="list-disc list-inside text-gray-300">{tech.benefits.map((b, idx) => <li key={idx}>{b}</li>)}</ul>
                  </motion.article>
                ))}
              </motion.div>
            </div>
          </section>

          <div className="py-12 px-4"><TechStackAccordionForSmartHome /></div>

        </div>
      </main>
    </>
  );
}